import { cloneBoard } from './board';
import { findMatches } from './matches';
import { TILE_KIND, TILE_SPECIAL } from './type';

function _isEmptyTile(t) {
  // The engine may represent empties either as null or as a placeholder tile.
  if (t == null) return true;

  // Common placeholder patterns (defensive)
  if (t.isEmpty === true) return true;
  if (t.placeholder === true) return true;

  const kind = String(t.kind ?? '').toLowerCase();
  if (kind === 'empty' || kind === 'hole' || kind === 'blank' || kind === 'void') return true;

  const key = t.key;
  if (key == null && !t.special && kind === '') return true;
  if (typeof key === 'string') {
    const ks = key.trim().toLowerCase();
    if (ks === '+' || ks === 'empty' || ks === 'hole') return true;
  }

  return false;
}

function _getTile(board, p) {
  return board?.grid?.[p?.r]?.[p?.c] ?? null;
}

function _isNonSwappableMechanic(board, p) {
  const mechanic = board?.meta?.mechanicState?.[p?.r]?.[p?.c];
  return mechanic?.type === 'volcanic' || mechanic?.type === 'stone';
}

export function isAdjacent(a, b) {
  if (!a || !b) return false;
  if (
    !Number.isInteger(a.r) ||
    !Number.isInteger(a.c) ||
    !Number.isInteger(b.r) ||
    !Number.isInteger(b.c)
  ) return false;
  const dr = Math.abs(a.r - b.r);
  const dc = Math.abs(a.c - b.c);
  return (dr + dc) === 1;
}

export function isPositionInBounds(board, position) {
  return !!(
    board &&
    Number.isInteger(board.rows) &&
    Number.isInteger(board.cols) &&
    position &&
    Number.isInteger(position.r) &&
    Number.isInteger(position.c) &&
    position.r >= 0 &&
    position.c >= 0 &&
    position.r < board.rows &&
    position.c < board.cols
  );
}

export function applySwap(board, a, b) {
  const nb = cloneBoard(board);
  const t1 = nb.grid[a.r][a.c];
  const t2 = nb.grid[b.r][b.c];
  nb.grid[a.r][a.c] = t2;
  nb.grid[b.r][b.c] = t1;
  return nb;
}

function _hasSpecialAt(board, p) {
  const t = _getTile(board, p);
  if (!t || _isEmptyTile(t)) return false;
  return (
    t.special === TILE_SPECIAL.STRIPE_H ||
    t.special === TILE_SPECIAL.STRIPE_V ||
    t.special === TILE_SPECIAL.STAR
  );
}

export function isSwapValid(board, a, b) {
  if (!isPositionInBounds(board, a) || !isPositionInBounds(board, b)) return false;
  if (!isAdjacent(a, b)) return false;
  if (_isNonSwappableMechanic(board, a) || _isNonSwappableMechanic(board, b)) return false;

  // Allow swaps into empty cells (slide into hole)
  const tA = _getTile(board, a);
  const tB = _getTile(board, b);
  if (_isEmptyTile(tA) || _isEmptyTile(tB)) {
    return true;
  }

  // Si alguno de los 2 tiles es especial, permitimos el swap (modo Candy).
  // Esto evita estados donde el tablero parece "muerto" aunque queden movimientos.
  if (_hasSpecialAt(board, a) || _hasSpecialAt(board, b)) return true;

  const swapped = applySwap(board, a, b);
  const swapAxis = a.r !== b.r ? 'y' : 'x';

  // IMPORTANT:
  // A valid swap can create a match centered on either endpoint (a or b).
  // If we only check one side (swapTo: b), we can miss real moves and
  // incorrectly think the board is "dead".
  const matchesB = findMatches(swapped, { swapTo: b, swapAxis }) || [];
  if (matchesB.length > 0) return true;

  const matchesA = findMatches(swapped, { swapTo: a, swapAxis }) || [];
  return matchesA.length > 0;
}

export function isHintSwapValid(board, a, b) {
  if (!isPositionInBounds(board, a) || !isPositionInBounds(board, b) || !isAdjacent(a, b)) {
    return false;
  }
  if (_isEmptyTile(_getTile(board, a)) || _isEmptyTile(_getTile(board, b))) {
    return false;
  }
  return isSwapValid(board, a, b);
}

export function swapWithEvents(board, a, b) {
  if (!isPositionInBounds(board, a) || !isPositionInBounds(board, b) || !isAdjacent(a, b)) {
    return {
      valid: false,
      events: [{ type: 'swap_invalid', a, b }],
      board,
    };
  }
  if (_isNonSwappableMechanic(board, a) || _isNonSwappableMechanic(board, b)) {
    return { valid: false, events: [{ type: 'swap_invalid_mechanic', a, b }], board };
  }

  const swapped = applySwap(board, a, b);
  const swapAxis = a.r !== b.r ? 'y' : 'x';

  // Check both endpoints (see isSwapValid) so we don't reject real moves.
  const matchesB = findMatches(swapped, { swapTo: b, swapAxis }) || [];
  const matchesA = findMatches(swapped, { swapTo: a, swapAxis }) || [];
  const matches = matchesB.length ? matchesB : matchesA;

  // Allow sliding into empty/placeholder cells (keeps "holes" playable)
  const tA0 = _getTile(board, a);
  const tB0 = _getTile(board, b);
  if (_isEmptyTile(tA0) || _isEmptyTile(tB0)) {
    return {
      valid: true,
      events: [{ type: 'swap', a, b }],
      board: swapped,
    };
  }

  // Permitir swaps con especiales aunque no generen 3-match.
  // (La activación/efecto del especial se resuelve en el motor/UI.)
  if (_hasSpecialAt(board, a) || _hasSpecialAt(board, b)) {
    return {
      valid: true,
      events: [{ type: 'swap', a, b }],
      board: swapped,
    };
  }

  if (matches.length === 0) {
    // swap -> no match -> swap back
    return {
      valid: false,
      events: [
        { type: 'swap', a, b },
        { type: 'swap_back', a, b },
      ],
      board,
    };
  }

  return {
    valid: true,
    events: [{ type: 'swap', a, b }],
    board: swapped,
  };
}

// Opcional (lo usaremos para detectar "tablero muerto")
export function listAdjacentPairs(board) {
  const pairs = [];
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      if (c + 1 < board.cols) pairs.push([{ r, c }, { r, c: c + 1 }]);
      if (r + 1 < board.rows) pairs.push([{ r, c }, { r: r + 1, c }]);
    }
  }
  return pairs;
}

export function hasAnyValidMove(board) {
  // 1) Sliding into empty cells is always a valid move
  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      const t = board.grid[r][c];
      if (!_isEmptyTile(t)) continue;

      if (r > 0 && !_isEmptyTile(board.grid[r - 1][c])) return true;
      if (r + 1 < board.rows && !_isEmptyTile(board.grid[r + 1][c])) return true;
      if (c > 0 && !_isEmptyTile(board.grid[r][c - 1])) return true;
      if (c + 1 < board.cols && !_isEmptyTile(board.grid[r][c + 1])) return true;
    }
  }

  // 2) Fallback to classic Candy-Crush-style swap validation
  const pairs = listAdjacentPairs(board);
  for (const [a, b] of pairs) {
    if (isSwapValid(board, a, b)) return true;
  }
  return false;
}


// =============================
// Tablero jugable (anti-dead)
// =============================

function _shuffleInPlace(arr, rng) {
  const rand = () => {
    try {
      if (rng && typeof rng.float === 'function') return rng.float();
      if (rng && typeof rng.random === 'function') return rng.random();
    } catch (_) {}
    return Math.random();
  };

  for (let i = arr.length - 1; i > 0; i--) {
    const r = rand();
    const j = Math.floor(r * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

function _hasImmediateMatches(board) {
  try {
    const m = findMatches(board) || [];
    return m.length > 0;
  } catch {
    return false;
  }
}

function _shuffleSignTiles(board, rng) {
  const nb = cloneBoard(board);

  // Guardamos posiciones y keys de SIGN para permutar sin tocar especiales.
  const positions = [];
  const keys = [];

  for (let r = 0; r < nb.rows; r++) {
    for (let c = 0; c < nb.cols; c++) {
      const t = nb.grid[r]?.[c];
      if (!t) continue;
      if (t.kind !== TILE_KIND.SIGN) continue;
      positions.push({ r, c });
      keys.push(t.key);
    }
  }

  _shuffleInPlace(keys, rng);

  for (let i = 0; i < positions.length; i++) {
    const { r, c } = positions[i];
    const t = nb.grid[r][c];
    nb.grid[r][c] = { ...t, key: keys[i] };
  }

  return nb;
}

/**
 * Si el tablero queda sin movimientos posibles, lo remezcla (solo SIGN) hasta
 * encontrar un tablero:
 *  - sin matches inmediatos
 *  - con al menos 1 swap válido
 *
 * Importante: esto NO consume movimientos del jugador.
 * Usalo cuando el board quedó estable (post-cascade/refill) o al inicializar.
 */
export function ensurePlayableBoard(board, opts = {}) {
  const maxAttempts = Number.isFinite(opts.maxAttempts) ? opts.maxAttempts : 60;
  const rng = opts && (opts.rng || opts.random || opts._rng) ? (opts.rng || opts.random || opts._rng) : null;

  // Si ya tiene jugadas posibles, no tocamos nada.
  if (hasAnyValidMove(board)) return board;

  for (let i = 0; i < maxAttempts; i++) {
    const candidate = _shuffleSignTiles(board, rng);
    if (_hasImmediateMatches(candidate)) continue;
    if (!hasAnyValidMove(candidate)) continue;
    return candidate;
  }

  // Fallback: devolver tal cual si no encontramos (debería ser rarísimo)
  return board;
}
